import React, { useState } from "react";
import "./PrinterSolution.css";
import ServiceContactModal from "../ServiceContactModal/ServiceContactModal";

const printerSolutions = [
  {
    id: "managed-print",
    title: "Managed Print Services",
    tag: "End-to-end print management",
    description:
      "We take full ownership of your print environment—from device selection to monitoring, toner, and break-fix—so your team can focus on work, not printers.",
    image:
      "https://images.pexels.com/photos/193355/pexels-photo-193355.jpeg?auto=compress&cs=tinysrgb&w=800",
    highlights: ["Centralized monitoring", "Automated supplies", "Predictable monthly cost"],
  },
  {
    id: "cloud-print",
    title: "Cloud Print Management",
    tag: "Print anywhere, securely",
    description:
      "Enable secure printing from any device, any location, without complex on-prem print servers. Ideal for hybrid and multi-location teams.",
    image:
      "https://images.pexels.com/photos/7014034/pexels-photo-7014034.jpeg?auto=compress&cs=tinysrgb&w=800",
    highlights: ["Serverless printing", "User-based rules", "Works across branches"],
  },
  {
    id: "secure-print",
    title: "Secure & Compliant Printing",
    tag: "Protect documents at the device",
    description:
      "Protect sensitive documents with user authentication, pull printing, and detailed audit trails that support compliance and governance.",
    image:
      "https://images.pexels.com/photos/7014410/pexels-photo-7014410.jpeg?auto=compress&cs=tinysrgb&w=800",
    highlights: ["Badge / PIN release", "Audit logs", "Policy-based access"],
  },
  {
    id: "fleet-optimization",
    title: "Printer Fleet Optimization",
    tag: "Right-size your printer fleet",
    description:
      "We analyze volumes, locations, and device health to reduce the number of printers, standardize models, and cut both capex and opex.",
    image:
      "https://images.pexels.com/photos/7014415/pexels-photo-7014415.jpeg?auto=compress&cs=tinysrgb&w=800",
    highlights: ["Fewer models, easier support", "Lower TCO", "Better device placement"],
  },
  {
    id: "support-maintenance",
    title: "Proactive Support & Maintenance",
    tag: "Stay up, not stuck",
    description:
      "Remote monitoring, preventive maintenance and SLA-based on-site support to keep your print environment running smoothly.",
    image:
      "https://images.pexels.com/photos/380769/pexels-photo-380769.jpeg?auto=compress&cs=tinysrgb&w=800",
    highlights: ["24/7 monitoring options", "Automatic alerts", "SLA-based response"],
  },

  // ⭐ NEW SERVICE ADDED ⭐
  {
    id: "document-automation",
    title: "Document Workflow Automation",
    tag: "Digitize & automate document flows",
    description:
      "Automate manual document-heavy processes such as scanning, routing, approvals and archival. Reduce paper, save time and improve compliance.",
    image:
      "https://images.pexels.com/photos/373076/pexels-photo-373076.jpeg?auto=compress&cs=tinysrgb&w=800",
    highlights: ["Scan-to-cloud workflows", "Auto-routing rules", "Compliance-ready archives"],
  },
];


const PrinterSolution = () => {

    const [open, setOpen] = useState(false);

  return (
    <>
    <section className="ps-section">
      <div className="ps-inner">
        {/* Intro copy */}
        <div className="ps-header">
          <p className="ps-eyebrow">Print Infrastructure</p>
          <h2 className="ps-title">Smart Printer & Document Solutions</h2>
          <p className="ps-sub">
            From a single office to multi-site enterprises, we design, deploy and manage
            printer environments that are secure, cost-efficient and easy to run.
          </p>

          <div className="ps-meta">
            <div className="ps-meta-item">
              <span className="ps-meta-number">30–40%</span>
              <span className="ps-meta-label">Typical print cost savings</span>
            </div>
            <div className="ps-meta-item">
              <span className="ps-meta-number">99.5%</span>
              <span className="ps-meta-label">Average device uptime</span>
            </div>
            <div className="ps-meta-item">
              <span className="ps-meta-number">24/7</span>
              <span className="ps-meta-label">Monitoring & ticketing options</span>
            </div>
          </div>
        </div>

        {/* Grid of solutions */}
        <div className="ps-grid">
          {printerSolutions.map((solution) => (
            <article key={solution.id} className="ps-card">
              <div className="ps-image-wrap">
                <img
                  src={solution.image}
                  alt={solution.title}
                  loading="lazy"
                  className="ps-image"
                />
                <span className="ps-tag">{solution.tag}</span>
              </div>
              <div className="ps-card-body">
                <h3 className="ps-card-title">{solution.title}</h3>
                <p className="ps-card-desc">{solution.description}</p>

                <ul className="ps-highlights">
                  {solution.highlights.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>

                <button
                  type="button"
                  className="ps-cta"
                  onClick={() => setOpen(true)}>
                  Talk to us about {solution.title.toLowerCase()}
                  <span className="ps-cta-arrow">›</span>
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* Process strip */}
        <div className="ps-process">
          <h3>How we roll out your printer solution</h3>
          <div className="ps-process-steps">
            <div className="ps-step">
              <span className="ps-step-badge">1</span>
              <h4>Assess & Discover</h4>
              <p>
                We review volumes, locations, existing devices and spend to identify
                quick wins and longer-term opportunities.
              </p>
            </div>
            <div className="ps-step">
              <span className="ps-step-badge">2</span>
              <h4>Design & Propose</h4>
              <p>
                A right-sized printer fleet, software stack and support model tailored to
                your sites, users and security needs.
              </p>
            </div>
            <div className="ps-step">
              <span className="ps-step-badge">3</span>
              <h4>Deploy & Manage</h4>
              <p>
                We deploy, train your teams and stay on as your single point of contact
                for support, monitoring and optimization.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
    <ServiceContactModal
        isOpen={open}
        onClose={() => setOpen(false)}
        defaultService="Managed Print Services"
      />
   </>
  );
};

export default PrinterSolution;
